import { useMemo } from "react";
import { AlertTriangle, Download, ShieldCheck, Scale, Network } from "lucide-react";
import { Button, Card, EmptyState, Label, Section, Tag } from "../components/ui";
import { buildBalanceOverview, type ConcentrationLevel, type WeightShare } from "../lib/balance";
import { buildBalanceReport, buildParticipationReport, downloadCsv } from "../lib/reports";
import type { LexData } from "../lib/types";

const levelTag: Record<ConcentrationLevel, string> = { balanced: "Balanced", watch: "Watch", breach: "Over cap" };

export function BalancePanel({ data }: { data: LexData }) {
  const overview = useMemo(() => buildBalanceOverview(data), [data]);

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={() => downloadCsv(buildBalanceReport(data))}>
          <span className="inline-flex items-center gap-2"><Download className="h-4 w-4" /> Balance report (CSV)</span>
        </Button>
        <Button type="button" variant="secondary" onClick={() => downloadCsv(buildParticipationReport(data))}>
          <span className="inline-flex items-center gap-2"><Download className="h-4 w-4" /> Participation report (CSV)</span>
        </Button>
      </div>

      <Section title="Network concentration" kicker="Governance health">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={<Network className="h-4 w-4" />} label="Active full members" value={String(overview.activeFullMemberCount)} detail="One voting account per firm" />
          <StatCard icon={<Scale className="h-4 w-4" />} label="Single-firm cap" value={`${(overview.firmCapShare * 100).toFixed(0)}%`} detail={`Procuration cap ${overview.delegationCap} per delegate`} />
          <StatCard
            icon={<ShieldCheck className="h-4 w-4" />}
            label="Concentration index"
            value={overview.herfindahlIndex.toFixed(3)}
            detail={overview.concentrationLabel}
            tone={overview.herfindahlIndex >= 0.25 ? "red" : overview.herfindahlIndex >= 0.15 ? "amber" : "green"}
          />
          <StatCard
            icon={<AlertTriangle className="h-4 w-4" />}
            label="Open balance alerts"
            value={String(overview.alerts.length)}
            detail={overview.alerts.some((alert) => alert.severity === "breach") ? "Includes cap breaches" : "Within guidelines"}
            tone={overview.alerts.some((alert) => alert.severity === "breach") ? "red" : overview.alerts.length > 0 ? "amber" : "green"}
          />
        </div>
      </Section>

      {overview.alerts.length > 0 ? (
        <Section title="Alerts" kicker="Needs committee attention">
          <div className="grid gap-2">
            {overview.alerts.map((alert, index) => (
              <Card key={`${alert.dimension}-${index}`} className={alert.severity === "breach" ? "border-rose-200 bg-rose-50/40" : "border-amber-200 bg-amber-50/40"}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`mt-0.5 h-4 w-4 ${alert.severity === "breach" ? "text-rose-700" : "text-amber-700"}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-ink">{alert.label}</span>
                      <Tag>{alert.dimension}</Tag>
                    </div>
                    <p className="mt-1 text-[13px] leading-5 text-secondary">{alert.message}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Section>
      ) : (
        <Section title="Alerts" kicker="Needs committee attention">
          <EmptyState>No firm, jurisdiction or affiliation group is approaching its holding cap. The network is well distributed.</EmptyState>
        </Section>
      )}

      <Section title="Voting weight by firm" kicker="Controlled weight = own + procurations held">
        <ShareTable rows={overview.byFirm} cap={overview.firmCapShare} />
      </Section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="By jurisdiction" kicker={`Guideline ${(overview.jurisdictionThreshold * 100).toFixed(0)}%`}>
          <ShareTable rows={overview.byJurisdiction} cap={overview.jurisdictionThreshold} compact />
        </Section>
        <Section title="By affiliation group" kicker={`Guideline ${(overview.firmCapShare * 100).toFixed(0)}%`}>
          {overview.byAffiliation.length > 0 ? <ShareTable rows={overview.byAffiliation} cap={overview.firmCapShare} compact /> : <EmptyState>No active affiliation groups recorded.</EmptyState>}
        </Section>
      </div>
    </>
  );
}

function StatCard({ icon, label, value, detail, tone = "neutral" }: { icon: React.ReactNode; label: string; value: string; detail: string; tone?: "neutral" | "green" | "amber" | "red" }) {
  const toneClass = tone === "red" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : tone === "green" ? "text-emerald-700" : "text-ink";
  return (
    <Card>
      <div className="mb-2 flex items-center gap-2 text-muted">
        {icon}
        <Label>{label}</Label>
      </div>
      <div className={`text-2xl font-semibold tracking-[-0.02em] ${toneClass}`}>{value}</div>
      <div className="mt-1 text-[12px] leading-4 text-secondary">{detail}</div>
    </Card>
  );
}

function ShareTable({ rows, cap, compact = false }: { rows: WeightShare[]; cap: number; compact?: boolean }) {
  if (rows.length === 0) return <EmptyState>No data.</EmptyState>;
  const max = Math.max(cap, ...rows.map((row) => row.share), 0.01);
  return (
    <Card className="p-0">
      <div className="divide-y divide-line">
        {rows.map((row) => (
          <div key={row.id} className="flex items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-semibold text-ink">{row.label}</span>
                {row.level !== "balanced" ? <Tag>{levelTag[row.level]}</Tag> : null}
              </div>
              <div className="mt-0.5 text-[12px] text-secondary">{row.detail}</div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#EDEFF3]">
                <div
                  className={`h-full rounded-full ${row.level === "breach" ? "bg-rose-500" : row.level === "watch" ? "bg-amber-500" : "bg-primary"}`}
                  style={{ width: `${Math.min(100, (row.share / max) * 100)}%` }}
                />
              </div>
            </div>
            <div className="w-28 shrink-0 text-right">
              <div className="text-sm font-semibold text-ink">{(row.share * 100).toFixed(1)}%</div>
              {!compact && row.delegatedWeight > 0 ? <div className="text-[11px] text-secondary">+{row.delegatedWeight.toFixed(2)} via procuration</div> : <div className="text-[11px] text-secondary">{row.controlledWeight.toFixed(2)} weight</div>}
            </div>
          </div>
        ))}
        <div className="px-4 py-2 text-[11px] text-muted">Cap line at {(cap * 100).toFixed(0)}%.</div>
      </div>
    </Card>
  );
}
